import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close,
  Description,
  Download,
  OpenInNew,
} from '@mui/icons-material';
import { cvAPI } from '../../services/api';
import { tokens } from '../../theme';

/**
 * Resume preview and download, shared by every role that reviews candidates.
 *
 * Resumes live in Google Cloud Storage in a bucket that blocks public access, so
 * there is no URL a browser can follow on its own. Two routes back:
 *
 *  - preview fetches the file as a Blob through axios and renders it from an
 *    object URL, rather than letting an <iframe> or <a> point straight at the
 *    API. That is not incidental: CRA's dev server only proxies requests that do
 *    not accept HTML, so a frame or link navigation to /api/... is answered with
 *    index.html and the React app renders inside the frame instead of the
 *    document. An XHR is proxied, carries the session cookie, and behaves the
 *    same once built.
 *  - download asks the API for a short-lived signed URL and hands that to the
 *    browser, so a large resume is fetched straight from storage instead of
 *    being streamed through the API a second time. It falls back to the Blob if
 *    signing is unavailable.
 *
 * Access is enforced server-side per role — for the signed URL too, which is
 * only issued to a caller already allowed to read that resume — so this
 * component does not decide who may see what. It only reports what was refused.
 */

const humanSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Read an error body that arrived as a Blob because responseType was 'blob'. */
const readBlobError = async (error) => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text())?.error;
    } catch {
      return null;
    }
  }
  return data?.error || null;
};

/**
 * Load a candidate's resume once and expose it as an object URL.
 * Revokes the URL when it changes or the holder unmounts, so blobs are not leaked.
 */
const useResumeBlob = (candidateId, active) => {
  const [state, setState] = useState({ url: null, loading: false, error: null });
  const urlRef = useRef(null);

  const release = useCallback(() => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!active || !candidateId) return undefined;

    let cancelled = false;
    setState({ url: null, loading: true, error: null });

    cvAPI
      .fetchResumeBlob(candidateId)
      .then((response) => {
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(response.data);
        urlRef.current = objectUrl;
        setState({ url: objectUrl, loading: false, error: null });
      })
      .catch(async (error) => {
        if (cancelled) return;
        const message =
          (await readBlobError(error)) ||
          (error.response?.status === 404
            ? 'This resume is not available to you.'
            : 'The resume could not be loaded.');
        setState({ url: null, loading: false, error: message });
      });

    return () => {
      cancelled = true;
      release();
    };
  }, [candidateId, active, release]);

  return state;
};

/**
 * Save a URL under a chosen filename.
 *
 * `download` only names the file for a same-origin URL, which covers the Blob
 * case. A signed storage URL is cross-origin and the attribute is ignored — the
 * filename comes from the Content-Disposition header baked into the signature
 * instead, which is why the API signs one in.
 */
const saveAs = (url, filename) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'resume';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const ResumePreviewDialog = ({ open, onClose, candidateId, candidateName, meta }) => {
  const { url, loading, error } = useResumeBlob(candidateId, open);
  const previewable = meta?.document?.previewable;
  const filename = meta?.document?.original_filename || 'resume.pdf';

  const download = () => url && saveAs(url, filename);
  const openInTab = () => url && window.open(url, '_blank', 'noopener');

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Description fontSize="small" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }} noWrap>
              {candidateName ? `${candidateName} — resume` : 'Resume'}
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.color.inkSecondary }} noWrap>
              {filename}
              {meta?.document?.file_size ? ` · ${humanSize(meta.document.file_size)}` : ''}
            </Typography>
          </Box>
          <Tooltip title="Open in a new tab">
            <span>
              <IconButton size="small" onClick={openInTab} disabled={!url}>
                <OpenInNew fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Download">
            <span>
              <IconButton size="small" onClick={download} disabled={!url}>
                <Download fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: '75vh', bgcolor: tokens.color.surfaceSunken }}>
        {loading && (
          <Box sx={{ display: 'grid', placeItems: 'center', height: '100%', gap: 1.5 }}>
            <CircularProgress size={26} />
            <Typography variant="body2" sx={{ color: tokens.color.inkSecondary }}>
              Loading resume…
            </Typography>
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {!loading && !error && url && previewable && (
          <Box
            component="iframe"
            title={`Resume of ${candidateName || 'candidate'}`}
            src={url}
            sx={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
          />
        )}

        {!loading && !error && url && !previewable && (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">
              <strong>{filename}</strong> cannot be shown in the browser — only PDFs render inline.
              Download it to open in Word.
            </Alert>
            <Button variant="contained" startIcon={<Download />} sx={{ mt: 2 }} onClick={download}>
              Download {filename}
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" startIcon={<Download />} onClick={download} disabled={!url}>
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * View / Download buttons for a candidate's resume.
 *
 * Availability is checked first, so a candidate with no resume on file shows
 * "No resume" instead of a button that opens an error.
 */
const ResumeActions = ({
  candidateId,
  candidateName,
  size = 'small',
  variant = 'buttons', // 'buttons' | 'icons'
  autoCheck = true,
}) => {
  const [meta, setMeta] = useState(null);
  const [checking, setChecking] = useState(false);
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const check = useCallback(async () => {
    if (!candidateId) return;
    setChecking(true);
    try {
      const response = await cvAPI.getResumeMeta(candidateId);
      setMeta(response.data);
    } catch {
      setMeta({ has_resume: false });
    } finally {
      setChecking(false);
    }
  }, [candidateId]);

  useEffect(() => {
    if (autoCheck) check();
  }, [autoCheck, check]);

  // Download without opening the dialog. A signed URL first — the browser then
  // pulls the file straight from cloud storage rather than through the API — and
  // the Blob as a fallback if signing is not available.
  const downloadDirect = async () => {
    setDownloading(true);
    const filename = meta?.document?.original_filename || 'resume.pdf';
    try {
      const { data } = await cvAPI.getResumeDownloadUrl(candidateId);
      if (data?.url) {
        saveAs(data.url, data.filename || filename);
        return;
      }
      throw new Error('No signed URL returned');
    } catch {
      try {
        const response = await cvAPI.fetchResumeBlob(candidateId);
        const url = URL.createObjectURL(response.data);
        saveAs(url, filename);
        URL.revokeObjectURL(url);
      } catch {
        // Surfaced in the dialog, which shows the server's reason.
        setOpen(true);
      }
    } finally {
      setDownloading(false);
    }
  };

  if (checking && !meta) {
    return <CircularProgress size={16} />;
  }

  if (meta && !meta.has_resume) {
    return (
      <Typography variant="body2" sx={{ color: tokens.color.inkTertiary }}>
        No resume
      </Typography>
    );
  }

  if (meta?.document && meta.document.exists === false) {
    return (
      <Tooltip title="The record exists but the file is not in cloud storage">
        <Chip
          size="small"
          label="File missing"
          sx={{ bgcolor: tokens.color.dangerSoft, color: tokens.color.danger }}
        />
      </Tooltip>
    );
  }

  const dialog = (
    <ResumePreviewDialog
      open={open}
      onClose={() => setOpen(false)}
      candidateId={candidateId}
      candidateName={candidateName}
      meta={meta}
    />
  );

  if (variant === 'icons') {
    return (
      <>
        <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
          {/*
            A document glyph, not an eye.
            Every table that renders these icons also renders its own "view
            details" action immediately to the left, and that used the same
            Visibility icon -- so a row showed two identical eyes, one opening
            the candidate record and one opening the resume, with nothing but a
            hover tooltip to tell them apart. Description pairs with the
            Download beside it: both of these act on the file, the neighbouring
            action acts on the record.
          */}
          <Tooltip title="Preview resume">
            <IconButton size={size} onClick={() => setOpen(true)}>
              <Description fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download resume">
            <span>
              <IconButton size={size} onClick={downloadDirect} disabled={downloading}>
                {downloading ? <CircularProgress size={14} /> : <Download fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {dialog}
      </>
    );
  }

  return (
    <>
      <Box sx={{ display: 'inline-flex', gap: 1, flexWrap: 'wrap' }}>
        <Button size={size} variant="outlined" startIcon={<Description />} onClick={() => setOpen(true)}>
          View resume
        </Button>
        <Button
          size={size}
          variant="text"
          startIcon={downloading ? <CircularProgress size={14} /> : <Download />}
          onClick={downloadDirect}
          disabled={downloading}
        >
          Download
        </Button>
      </Box>
      {dialog}
    </>
  );
};

export default ResumeActions;
